import axiosInstance from '@/app/api/axiosInstance';
import { 
  ItemCollection, 
  ItemCollectionCreate, 
  ItemCollectionUpdate,
  AddItemToCollectionRequest,
  LinkCollectionToItemRequest
} from '@/app/types/itemCollection';
import { PaginatedResponse, ListQueryParams } from '@/app/types/api';

// List all item collections
export const listItemCollections = async (params?: ListQueryParams): Promise<ItemCollection[]> => {
  try {
    const response = await axiosInstance.get<ItemCollection[]>('/item-collections/', { 
      params 
    });
    return response.data;
  } catch (error) {
    console.error('Failed to list item collections:', error);
    throw error;
  }
};

// Get a single collection with optional items
export const getItemCollection = async (collectionId: number, includeItems: boolean = false): Promise<ItemCollection> => {
  try {
    const response = await axiosInstance.get<ItemCollection>(`/item-collections/${collectionId}`, {
      params: { include_items: includeItems }
    });
    return response.data;
  } catch (error) {
    console.error(`Failed to get item collection with ID ${collectionId}:`, error);
    throw error;
  }
};

// Create a new item collection
export const createItemCollection = async (data: ItemCollectionCreate): Promise<ItemCollection> => {
  try {
    const response = await axiosInstance.post<ItemCollection>('/item-collections/', data);
    return response.data;
  } catch (error) {
    console.error('Failed to create item collection:', error);
    throw error;
  }
};

// Update an existing item collection
export const updateItemCollection = async (collectionId: number, data: ItemCollectionUpdate): Promise<ItemCollection> => {
  try {
    const response = await axiosInstance.put<ItemCollection>(`/item-collections/${collectionId}`, data);
    return response.data;
  } catch (error) {
    console.error(`Failed to update item collection with ID ${collectionId}:`, error);
    throw error;
  }
};

// Delete an item collection
export const deleteItemCollection = async (collectionId: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/item-collections/${collectionId}`);
  } catch (error) {
    console.error(`Failed to delete item collection with ID ${collectionId}:`, error);
    throw error;
  }
};

// Add item to collection
export const addItemToCollection = async (
  collectionId: number, 
  data: AddItemToCollectionRequest
): Promise<void> => {
  try {
    await axiosInstance.post(`/item-collections/${collectionId}/items`, data);
  } catch (error) {
    console.error(`Failed to add item to collection ${collectionId}:`, error);
    throw error;
  }
};

// Remove item from collection
export const removeItemFromCollection = async (
  collectionId: number, 
  itemId: number
): Promise<void> => {
  try {
    await axiosInstance.delete(`/item-collections/${collectionId}/items/${itemId}`);
  } catch (error) {
    console.error(`Failed to remove item from collection ${collectionId}:`, error);
    throw error;
  }
};

// Link collection to item as related
export const linkCollectionToItem = async (
  itemId: number, 
  data: LinkCollectionToItemRequest
): Promise<void> => {
  try {
    await axiosInstance.post(`/item-collections/items/${itemId}/related-collections`, data);
  } catch (error) {
    console.error(`Failed to link collection to item ${itemId}:`, error);
    throw error;
  }
};

// Unlink related collection from item
export const unlinkCollectionFromItem = async (
  itemId: number, 
  collectionId: number
): Promise<void> => {
  try {
    await axiosInstance.delete(`/item-collections/items/${itemId}/related-collections/${collectionId}`);
  } catch (error) {
    console.error(`Failed to unlink collection from item ${itemId}:`, error);
    throw error;
  }
};

// Update item sort order in collection
export const updateItemSortOrder = async (
  collectionId: number, 
  itemId: number, 
  sortOrder: number
): Promise<void> => {
  try {
    await axiosInstance.patch(`/item-collections/${collectionId}/items/${itemId}/sort-order`, { 
      sort_order: sortOrder 
    });
  } catch (error) {
    console.error(`Failed to update item sort order in collection ${collectionId}:`, error);
    throw error;
  }
};